import { z } from 'zod';

import { selectSecretInputSchema } from '@/console/features/vaults/components/form-select-secret/form-select-secret.validation';
import {
  DeviceTypeEnum,
  FtpProtocolEnum,
  SmbProtocolEnum,
} from '@/shared/types/types.generated';
import { optionalString, requiredString } from '@/shared/utils/validation';

// Factory function that creates a protocol-specific configuration schema
const createProtocolConfigurationInputSchema = <T extends z.ZodTypeAny>(
  protocolEnum: T,
  defaultProtocol: z.infer<T>,
  defaultPort: number = 22,
) => {
  return selectSecretInputSchema
    .extend({
      ipAddress: z.string().optional(),
      port: z
        .union([z.string().transform((val) => parseInt(val, 10)), z.number()])
        .optional(),
      rootDirectory: z.string().optional(),
      protocol: protocolEnum.optional(),
    })
    .refine((data) => {
      // Check if values are different from defaults (non-default means user interaction)
      const hasNonDefaultIp = data.ipAddress && data.ipAddress !== '';
      const hasNonDefaultPort = data.port && data.port !== defaultPort;
      const hasNonDefaultRoot =
        data.rootDirectory && data.rootDirectory !== '/';
      const hasNonDefaultProtocol =
        data.protocol && data.protocol !== defaultProtocol;

      const hasAnyNonDefault =
        hasNonDefaultIp ||
        hasNonDefaultPort ||
        hasNonDefaultRoot ||
        hasNonDefaultProtocol;

      // Only require all fields if any non-default value is provided
      if (hasAnyNonDefault) {
        return (
          data.ipAddress && data.port && data.rootDirectory && data.protocol
        );
      }

      return true;
    })
    .refine(
      (data) => {
        if (data.ipAddress) {
          return z.string().ip({ version: 'v4' }).safeParse(data.ipAddress)
            .success;
        }
        return true;
      },
      { path: ['ipAddress'] },
    )
    .refine(
      (data) => {
        if (data.rootDirectory !== undefined) {
          return data.rootDirectory.length > 0;
        }
        return true;
      },
      { path: ['rootDirectory'] },
    );
};

// Create specific instances for FTP and SMB
export const connectionFTPConfigurationInputSchema =
  createProtocolConfigurationInputSchema(
    z.nativeEnum(FtpProtocolEnum),
    FtpProtocolEnum.Sftp,
    22,
  );

export const connectionSMBConfigurationInputSchema =
  createProtocolConfigurationInputSchema(
    z.nativeEnum(SmbProtocolEnum),
    SmbProtocolEnum.Smb,
    445,
  );

export const connectionConfigurationInputSchema = z.object({
  connectionConfiguration: z.object({
    ipAddress: requiredString().ip({ version: 'v4', message: 'ip_invalid' }),
    port: z.union([
      z.string().transform((val) => parseInt(val, 10)),
      z.number(),
    ]),
  }),
  ftpConfiguration: connectionFTPConfigurationInputSchema.optional(),
  smbConfiguration: connectionSMBConfigurationInputSchema.optional(),
  gatewayId: optionalString(),
  enableFtp: z.boolean().optional(),
  enableSmb: z.boolean().optional(),
});

export const createDeviceInputSchema = (isGroupRequired?: boolean) =>
  selectSecretInputSchema.extend({
    description: optionalString(),
    groupId: isGroupRequired ? requiredString() : optionalString(),
    name: requiredString(),
    vendorId: requiredString(),
    ideConfigId: requiredString(),
    deviceType: z.nativeEnum(DeviceTypeEnum),
    tags: z.array(requiredString()).optional(),
  });
